import { z } from 'zod';
import { AdminEmail, AdminReason, CurrentPassword, HttpsUrl, NewPassword } from './fields.js';
import { ProductKey, Uuid } from './primitives.js';
/**
 * Admin API contracts (request bodies + the response shapes the admin portal relies on).
 * Dates travel as ISO strings.
 */
export const AdminRoleName = z.enum(['admin', 'content_manager', 'reviewer', 'viewer']);
export const RoleGrantSchema = z
    .object({ role: AdminRoleName, productId: Uuid.nullable() })
    .strict();
const TOTP_MESSAGE = 'Enter the 6 digits from your authenticator app';
const TotpCode = z
    .string()
    .trim()
    .length(6, TOTP_MESSAGE)
    .regex(/^\d{6}$/, TOTP_MESSAGE);
// ---- auth -------------------------------------------------------------------------------------
export const LoginRequest = z.object({ email: AdminEmail, password: CurrentPassword }).strict();
export const TotpRequest = z.object({ code: TotpCode }).strict();
export const ChangePasswordRequest = z
    .object({ currentPassword: CurrentPassword, newPassword: NewPassword })
    .strict();
export const SessionStageName = z.enum(['pending_totp', 'pending_enrollment', 'active']);
export const MeResponse = z.object({
    user: z.object({
        id: Uuid,
        email: z.string(),
        displayName: z.string().nullable(),
        totpEnabled: z.boolean(),
    }),
    stage: SessionStageName,
    roles: z.array(RoleGrantSchema),
    permissions: z.array(z.object({ permission: z.string(), productId: Uuid.nullable() })),
    mfaFresh: z.boolean(),
    expiresAt: z.string(),
    csrfToken: z.string(),
});
export const EnrollmentResponse = z.object({ secret: z.string(), otpauthUri: z.string() });
// ---- products, bots, flags --------------------------------------------------------------------
export const ProductStatusName = z.enum(['draft', 'active', 'archived']);
export const CreateProductRequest = z
    .object({
    key: ProductKey,
    name: z.string().trim().min(2).max(100),
    timezone: z.string().max(64).optional(),
    defaultLocale: z
        .string()
        .regex(/^[a-z]{2}(-[A-Z]{2})?$/)
        .optional(),
})
    .strict();
export const UpdateProductRequest = z
    .object({
    name: z.string().trim().min(2).max(100).optional(),
    status: ProductStatusName.optional(),
    timezone: z.string().max(64).optional(),
})
    .strict();
export const ProductDto = z.object({
    id: Uuid,
    key: z.string(),
    name: z.string(),
    status: ProductStatusName,
    defaultLocale: z.string(),
    timezone: z.string(),
    configRevision: z.number(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** A BotFather token: the numeric bot id, a colon, then the secret. */
const BotToken = z
    .string()
    .trim()
    .max(200)
    .regex(/^\d{5,16}:[A-Za-z0-9_-]{30,64}$/, 'A BotFather token looks like 123456789:AAE…');
/** A numeric Telegram chat id such as -1001234567890 (stored as a bigint). */
const ChatId = z
    .string()
    .trim()
    .max(24)
    .regex(/^-?\d{5,19}$/, 'A numeric chat id like -1001234567890');
export const UpsertBotRequest = z
    .object({
    botUsername: z
        .string()
        .trim()
        .regex(/^[A-Za-z][A-Za-z0-9_]{3,31}$/, 'Telegram bot username'),
    token: BotToken.optional(),
    miniAppUrl: HttpsUrl.nullable().optional(),
    channelId: ChatId.nullable().optional(),
    groupId: ChatId.nullable().optional(),
})
    .strict();
export const BotDto = z.object({
    productId: Uuid,
    botUsername: z.string(),
    botId: z.string(),
    miniAppUrl: z.string().nullable(),
    channelId: z.string().nullable(),
    groupId: z.string().nullable(),
    tokenSet: z.boolean(),
    updatedAt: z.string(),
});
/** A feature-flag key such as `grand_mock.enabled`. */
export const FlagKey = z
    .string()
    .regex(/^[a-z][a-z0-9_.-]{1,63}$/, 'Lowercase letters, digits, _ . or -, 2-64 chars');
export const UpsertFlagRequest = z
    .object({
    enabled: z.boolean(),
    description: z.string().trim().max(500).nullable().optional(),
    rules: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
export const FlagDto = z.object({
    key: z.string(),
    enabled: z.boolean(),
    description: z.string().nullable(),
    rules: z.record(z.string(), z.unknown()),
    updatedAt: z.string(),
});
// ---- exam packs -------------------------------------------------------------------------------
export const PackUploadRequest = z
    .object({
    packYaml: z
        .string()
        .min(1)
        .max(512 * 1024),
    taxonomyYaml: z
        .string()
        .max(512 * 1024)
        .optional(),
    notes: z.string().trim().max(1000).optional(),
    activate: z.boolean().optional(),
})
    .strict();
export const IssueDto = z.object({ path: z.string(), message: z.string() });
export const PackChangeDto = z.object({
    path: z.string(),
    kind: z.enum(['added', 'removed', 'changed']),
    before: z.unknown().optional(),
    after: z.unknown().optional(),
});
export const PackDiffDto = z.object({
    changes: z.array(PackChangeDto),
    summary: z.object({ added: z.number(), removed: z.number(), changed: z.number() }),
});
export const PackSummaryDto = z.object({
    key: z.string(),
    version: z.string(),
    name: z.string(),
    exams: z.array(z.object({ key: z.string(), name: z.string(), launch: z.string() })),
    plans: z.array(z.object({ key: z.string(), priceInr: z.number(), priceStars: z.number() })),
});
export const PackCheckDto = z.object({
    ok: z.boolean(),
    errors: z.array(IssueDto),
    warnings: z.array(IssueDto),
    sha256: z.string(),
    summary: PackSummaryDto.nullable(),
    taxonomySource: z.enum(['uploaded', 'active_version', 'none']),
    diff: PackDiffDto.nullable(),
    against: z.object({ id: Uuid, version: z.string() }).nullable(),
    existingVersionId: Uuid.nullable(),
});
export const VersionDto = z.object({
    id: Uuid,
    productId: Uuid,
    packKey: z.string(),
    version: z.string(),
    sha256: z.string(),
    status: z.enum(['draft', 'active', 'archived']),
    notes: z.string().nullable(),
    warnings: z.array(IssueDto),
    createdBy: Uuid.nullable(),
    createdAt: z.string(),
    activatedBy: Uuid.nullable(),
    activatedAt: z.string().nullable(),
    archivedAt: z.string().nullable(),
});
// ---- config -----------------------------------------------------------------------------------
export const SetOverrideRequest = z
    .object({ path: z.string().trim().min(1).max(200), value: z.unknown(), reason: AdminReason })
    .strict()
    .refine((v) => v.value !== undefined, { message: 'value is required', path: ['value'] });
export const DeleteOverrideRequest = z
    .object({ path: z.string().trim().min(1).max(200), reason: AdminReason })
    .strict();
export const OverrideDto = z.object({
    path: z.string(),
    value: z.unknown(),
    reason: z.string(),
    actorId: Uuid.nullable(),
    updatedAt: z.string(),
});
// ---- admin users ------------------------------------------------------------------------------
export const CreateAdminUserRequest = z
    .object({
    email: AdminEmail,
    displayName: z.string().trim().max(100).optional(),
    password: NewPassword,
    roles: z.array(RoleGrantSchema).max(50),
})
    .strict();
export const SetRolesRequest = z.object({ roles: z.array(RoleGrantSchema).max(50) }).strict();
export const SetStatusRequest = z.object({ status: z.enum(['active', 'disabled']) }).strict();
export const AdminUserDto = z.object({
    id: Uuid,
    email: z.string(),
    displayName: z.string().nullable(),
    status: z.enum(['active', 'disabled']),
    totpEnabled: z.boolean(),
    lockedUntil: z.string().nullable(),
    lastLoginAt: z.string().nullable(),
    createdAt: z.string(),
    roles: z.array(RoleGrantSchema),
});
// ---- audit ------------------------------------------------------------------------------------
export const AuditQuerySchema = z
    .object({
    productId: Uuid.optional(),
    entityType: z.string().max(64).optional(),
    entityId: z.string().max(200).optional(),
    action: z.string().max(64).optional(),
    actorId: z.string().max(64).optional(),
    before: Uuid.optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
})
    .strict();
export const AuditEntryDto = z.object({
    id: Uuid,
    actorType: z.enum(['admin', 'system', 'user']),
    actorId: z.string().nullable(),
    productId: Uuid.nullable(),
    action: z.string(),
    entityType: z.string(),
    entityId: z.string().nullable(),
    before: z.unknown(),
    after: z.unknown(),
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
    requestId: z.string().nullable(),
    createdAt: z.string(),
});
export const AuditPageDto = z.object({
    entries: z.array(AuditEntryDto),
    nextCursor: Uuid.nullable(),
});
//# sourceMappingURL=admin.js.map